// Custom Element base
// TODO: Find a way to conditionally auto-add "options" to observedAttributes
class ElementBase extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.options = {};
    this.listeners = new Map();
    this.attributeChangedCallbacks = new Map();
  }

  connectedCallback() {
    this.log('Connecting');

    this.init();
  }

  disconnectedCallback() {
    this.log('Disconnecting');

    this.destroy();
  }

  static get observedAttributes() {
    return [];
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    const attrKey = kebabToCamel(attr);
    if (Object.keys(this.options).includes(attrKey)) {
      this.options[attrKey] = parseAttribute(newValue);
    }

    const attrCallback = this.attributeChangedCallbacks.get(attr);

    if (attrCallback) attrCallback(newValue, oldValue, attr);
  }

  addAttributeListener(attr, callback) {
    this.attributeChangedCallbacks.set(attr, callback.bind(this));
  }

  addEventListeners() {
    this.log('Adding event listeners');
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.addEventListener(event, callback);
      }
    }
  }

  removeEventListeners() {
    this.log('Removing event listeners');
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.removeEventListener(event, callback);
      }
    }
  }

  addListener(target, event, callback, bound = false) {
    const existingListeners = this.listeners.get(target);
    const listeners = {
      ...existingListeners,
      [event]: bound ? callback : callback.bind(this),
    };

    this.listeners.set(target, listeners);
  }

  addResizeListener(target, callback) {
    callback = debounce(callback.bind(this), RESIZE_DEBOUNCE_TIMER);

    this.addListener(target, 'resize', callback, true);
  }

  init() {
    this.log('Initializing');

    this.options = this.getOptions();
    this.addEventListeners();

    this.initialized = true;
  }

  destroy() {
    this.removeEventListeners();

    this.initialized = false;
  }

  defaults() {
    return {};
  }

  parseOption(option) {
    option = camelToKebab(option);

    if (!this.hasAttribute(option)) return null;
    const value = this.getAttribute(option);

    return parseAttribute(value);
  }

  getOptions() {
    const defaults = this.defaults();
    const options = {};
    for (const [prop, defaultValue] of Object.entries(defaults)) {
      options[prop] = this.parseOption(prop) ?? defaultValue;
    }

    return options;
  }

  log() {
    console.debug(`${this.constructor.name}:`, ...arguments);
  }

  error() {
    console.error(`${this.constructor.name} Error:`, ...arguments);
  }
}

class ObservedElement extends ElementBase {
  constructor() {
    super();

    this.observerList = new Map();
    this.observers = new Map();
  }

  init() {
    super.init();
    this.initObservers();
  }

  destroy(disconnectObservers = true) {
    super.destroy();

    if (disconnectObservers) {
      this.disconnectObservers();
    }
  }

  getObserver(type, target) {
    const observers = this.observers.get(type);

    if (!observers) {
      this.error(`Failed to get observer, no observer of type ${type} found`);
      return false;
    }

    const observer = observers.get(target);

    if (!observer) {
      this.error(`Failed to get ${type} observer, no observer found for target`, target);
      return false;
    }

    return observer;
  }

  addObserver(target, type, data) {
    if (!data.callback) {
      this.error(`Failed to add ${type} observer, no callback provided`);
      return;
    }

    data.callback = data.callback.bind(this);

    const existingObservers = this.observerList.get(type) || new Map();
    const observers = [...existingObservers, [target, data]];

    this.observerList.set(type, observers);
  }

  initObservers() {
    this.log('Initializing observers');
    for (const [type, observers] of this.observerList) {
      for (const [target, data] of observers) {
        this.initObserver(type, target, data);
      }
    }
  }

  initObserver(type, target, data) {
    let observer = null;

    switch (type) {
      case 'mutation':
        observer = this.initMutationObserver(target, data);
        break;
      case 'intersection':
        observer = this.initIntersectionObserver(target, data);
        break;
      case 'resize':
        observer = this.initResizeObserver(target, data);
        break;
    }

    if (!observer) {
      this.error(`Failed to initialize observer, the provided type "${type}" is not supported`);
      return;
    }

    const existingObservers = this.observers.get(type) || new Map();
    const observers = [...existingObservers, [target, observer]];

    this.observers.set(type, observers);
  }

  addMutationObserver(target, callback, { mutationType = 'childList', options = {} } = {}) {
    this.addObserver(target, 'mutation', {
      callback,
      mutationType,
      options: {
        ...options,
        [mutationType]: true,
      },
    });
  }

  initMutationObserver(target, { mutationType, callback, options } = {}) {
    const observer = new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        if (mutation.type === mutationType) {
          callback(mutation, observer);
        }
      }
    });

    this.observe(observer, target, options);

    return observer;
  }

  addIntersectionObserver(
    target,
    callback,
    { onIntersect = true, root = null, rootMargin = '0px 0px 0px 0px', threshold = 0.0 } = {}
  ) {
    this.addObserver(target, 'intersection', {
      callback,
      onIntersect,
      options: { root, rootMargin, threshold },
    });
  }

  initIntersectionObserver(target, { callback, onIntersect, options } = {}) {
    const observer = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          callback(entry, observer);
        } else if (!onIntersect) {
          callback(entry, observer);
        }
      }
    }, options);

    this.observe(observer, target);

    return observer;
  }

  addResizeObserver(target, callback, { debounced = true, options } = {}) {
    this.addObserver(target, 'resize', { callback, debounced, options });
  }

  initResizeObserver(target, { callback, debounced, options } = {}) {
    const resize = (entries, observer) => {
      for (const entry of entries) {
        callback(entry, observer);
      }
    };

    const observer = new ResizeObserver(
      debounced ? debounce(resize, RESIZE_DEBOUNCE_TIMER) : resize
    );

    this.observe(observer, target, options);

    return observer;
  }

  disconnectObservers() {
    this.log('Disconnecting observers');
    for (const [type, observers] of this.observers) {
      for (const [target, observer] of observers) {
        observer.disconnect();
      }
    }
  }

  normalizeTargets(target) {
    return isIterable(target) ? [...target] : [target];
  }

  observe(observer, targets, options = false) {
    targets = this.normalizeTargets(targets);
    console.log('TARGETS', targets);

    for (const target of targets) {
      if (options !== false) {
        observer.observe(target, options);
      } else {
        observer.observe(target);
      }
    }
  }
}
